import { useState, type ReactNode } from "react";
import { SlidersHorizontal, X, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

export interface MobileFilterChip {
  key: string;
  label: string;
  onRemove: () => void;
}

interface MobileFilterSheetProps {
  /** Number of active filters — drives the badge on the trigger button. */
  activeCount: number;
  /** Removable chips shown inline next to the Filters button. */
  chips?: MobileFilterChip[];
  /** Clears every filter. Disabled when activeCount is 0. */
  onClearAll?: () => void;
  /** The filter controls rendered inside the bottom sheet. */
  children: ReactNode;
  title?: string;
  className?: string;
}

/**
 * Mobile-only filter affordance: a single "Filters" button (with an active
 * count badge) that opens a bottom sheet containing the filter controls, plus
 * inline removable chips so the active state is visible without reopening.
 * Replaces the desktop horizontal-scroll filter toolbars on phones.
 */
export function MobileFilterSheet({
  activeCount,
  chips = [],
  onClearAll,
  children,
  title = "Filters",
  className,
}: MobileFilterSheetProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ""}`}>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className="h-9 shrink-0 gap-1.5"
            data-testid="button-mobile-filters"
          >
            <SlidersHorizontal className="h-4 w-4" />
            {title}
            {activeCount > 0 && (
              <Badge
                variant="secondary"
                className="ml-0.5 h-5 min-w-5 justify-center rounded-full px-1.5 text-[11px]"
                data-testid="badge-filter-count"
              >
                {activeCount}
              </Badge>
            )}
          </Button>
        </SheetTrigger>
        <SheetContent
          side="bottom"
          className="max-h-[85vh] overflow-y-auto rounded-t-2xl"
        >
          <SheetHeader className="mb-4 text-left">
            <SheetTitle>{title}</SheetTitle>
          </SheetHeader>
          <div className="space-y-5 pb-2">{children}</div>
          <SheetFooter className="mt-6 flex-row gap-2">
            <Button
              variant="outline"
              className="h-11 flex-1"
              onClick={() => onClearAll?.()}
              disabled={activeCount === 0}
              data-testid="button-clear-filters"
            >
              Clear all
            </Button>
            <Button
              className="h-11 flex-1"
              onClick={() => setOpen(false)}
              data-testid="button-apply-filters"
            >
              Done
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={c.onRemove}
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-xs text-foreground active:bg-muted/70"
          data-testid={`chip-filter-${c.key}`}
        >
          {c.label}
          <X className="h-3 w-3 opacity-60" />
        </button>
      ))}
    </div>
  );
}

/** A labelled section inside a MobileFilterSheet. */
export function MobileFilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{label}</p>
      {children}
    </div>
  );
}

interface MobileRegionPickerProps {
  regions: { id: string; name: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
}

/**
 * Searchable multi-select region list for use inside a MobileFilterSheet.
 * Mirrors the desktop region popover behaviour (empty selection = "All").
 */
export function MobileRegionPicker({ regions, selected, onChange }: MobileRegionPickerProps) {
  const [search, setSearch] = useState("");
  const filtered = search.trim()
    ? regions.filter((r) => r.name.toLowerCase().includes(search.trim().toLowerCase()))
    : regions;
  const toggle = (id: string) => {
    onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };
  return (
    <div className="rounded-lg border border-border">
      <div className="relative border-b border-border p-2">
        <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search regions"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="h-9 pl-9"
          data-testid="input-mobile-region-search"
        />
      </div>
      <div className="max-h-56 space-y-0.5 overflow-y-auto p-1.5">
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm active:bg-muted"
          onClick={() => onChange([])}
          data-testid="option-mobile-region-all"
        >
          <Checkbox checked={selected.length === 0} className="pointer-events-none" />
          <span>All Regions</span>
        </button>
        {filtered.map((r) => (
          <button
            key={r.id}
            type="button"
            className="flex w-full items-center gap-2 rounded-md p-2 text-left text-sm active:bg-muted"
            onClick={() => toggle(r.id)}
            data-testid={`option-mobile-region-${r.id}`}
          >
            <Checkbox checked={selected.includes(r.id)} className="pointer-events-none" />
            <span className="truncate">{r.name}</span>
          </button>
        ))}
        {filtered.length === 0 && (
          <p className="px-2 py-3 text-center text-xs text-muted-foreground">No regions match</p>
        )}
      </div>
    </div>
  );
}
